import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MaskedInput } from '@/components/ui/masked-input';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [telefone, setTelefone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateTelefone = (tel) => {
    const digits = tel.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 11;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!isLogin) {
        // Validações do cadastro
        if (!companyName.trim()) {
          toast.error('Nome da empresa é obrigatório');
          setLoading(false);
          return;
        }
        if (!validateTelefone(telefone)) {
          toast.error('Telefone inválido. Use o formato (XX) XXXXX-XXXX');
          setLoading(false);
          return;
        }
      }

      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email, password } 
        : { email, name, password, company_name: companyName, telefone };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      toast.success(isLogin ? 'Login realizado com sucesso!' : 'Conta criada com sucesso!');
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao autenticar');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = () => {
    setIsLogin(!isLogin);
    // Reset fields when switching modes
    if (isLogin) {
      setName('');
      setCompanyName('');
      setTelefone('');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="SUA Gestão Logo" 
              className="h-14 w-auto object-contain"
              data-testid="auth-logo"
            />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-primary">SUA Gestão</h1>
          <p className="text-muted-foreground mt-2">Sistema exclusivo para parceiros SUA Assessoria</p>
        </div>

        <Card data-testid="auth-card">
          <CardHeader>
            <CardTitle data-testid="auth-title">{isLogin ? 'Entrar' : 'Criar Conta'}</CardTitle>
            <CardDescription data-testid="auth-description">
              {isLogin ? 'Entre com suas credenciais' : 'Preencha os dados para criar sua conta'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome Completo <span className="text-destructive">*</span></Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Digite seu nome completo"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required={!isLogin}
                      data-testid="auth-name-input"
                      className="input-focus"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyName">
                      Nome da Empresa <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Ex: Empresa XYZ ou Autônomo"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required={!isLogin}
                      data-testid="auth-company-input"
                      className="input-focus"
                    />
                    <p className="text-xs text-muted-foreground">
                      <strong>Obrigatório:</strong> Se não tiver empresa, digite "Autônomo"
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefone">
                      Telefone/WhatsApp <span className="text-destructive">*</span>
                    </Label>
                    <MaskedInput
                      id="telefone"
                      mask="phone"
                      placeholder="(11) 99999-9999"
                      value={telefone}
                      onChange={(e) => setTelefone(e.target.value)}
                      required={!isLogin}
                      data-testid="auth-telefone-input"
                      className="input-focus"
                    />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="auth-email-input"
                    className="input-focus pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="auth-password-input"
                    className="input-focus pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full btn-transition"
                disabled={loading}
                data-testid="auth-submit-button"
              >
                {loading ? 'Aguarde...' : isLogin ? 'Entrar' : 'Criar Conta'}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm">
              <button
                type="button"
                onClick={handleToggleMode}
                className="text-primary hover:underline"
                data-testid="auth-toggle-button"
              >
                {isLogin ? 'Não tem conta? Criar uma' : 'Já tem conta? Entrar'}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Footer com copyright */}
        <p className="text-center text-sm text-muted-foreground mt-8">
          © 2026 SUA Assessoria. Todos os direitos reservados.
        </p>
      </motion.div>
    </div>
  );
}
